/**
 * Pure transform: converts a raw workflowRuns Convex document into a
 * RefinementRunCard view-model suitable for direct use by screen components.
 *
 * Reads status labels/colors from STATUS_CONFIG and kind labels from KIND_CONFIG.
 * Computes isTerminal via isTerminalStatus from shared/workflowStatus.ts.
 * Emits a structured WARN log (transform.unknownStatus) on _unknown fallback.
 *
 * Never throws on any valid Convex document shape.
 */
import type { Doc } from "../../../../../convex/_generated/dataModel";
import type { RefinementRunCard } from "../types/refinementRun";
import { isTerminalStatus } from "../../../../../shared/workflowStatus";
import { KIND_CONFIG } from "../constants/kind";

// ---------------------------------------------------------------------------
// STATUS_CONFIG — workflow-run status display configuration (FR-005 / V7)
//
// Maps raw backend status strings to user-facing labels, colors, and
// background colors. The `_unknown` entry provides the graceful fallback
// for unrecognized statuses introduced by future pipeline versions.
// ---------------------------------------------------------------------------

interface WorkflowStatusEntry {
  /** User-facing display label. */
  label: string;
  /** CSS color value for text/icon rendering. */
  color: string;
  /** CSS background color value for badge rendering. */
  bgColor: string;
}

const STATUS_CONFIG: Record<string, WorkflowStatusEntry> = {
  running: {
    label: "In progress",
    color: "var(--color-action-blue)",
    bgColor: "var(--color-action-blue-bg)",
  },
  completed: {
    label: "Succeeded",
    color: "var(--color-success)",
    bgColor: "var(--color-success-bg)",
  },
  completed_with_warnings: {
    label: "Succeeded (warnings)",
    color: "var(--color-warning)",
    bgColor: "var(--color-warning-bg)",
  },
  completed_oscillating: {
    label: "Converged (oscillating)",
    color: "var(--color-warning)",
    bgColor: "var(--color-warning-bg)",
  },
  failed: {
    label: "Failed",
    color: "var(--color-error)",
    bgColor: "var(--color-error-bg)",
  },
  cancelled: {
    label: "Cancelled",
    color: "var(--color-muted)",
    bgColor: "var(--color-muted-bg)",
  },
  _unknown: {
    label: "Unknown",
    color: "var(--color-muted)",
    bgColor: "var(--color-muted-bg)",
  },
};

// ---------------------------------------------------------------------------
// Transform
// ---------------------------------------------------------------------------

/**
 * Converts a raw `workflowRuns` Convex document into a `RefinementRunCard`
 * view-model. Pure function — no side effects, no network calls, never throws.
 *
 * Degraded inputs produce graceful fallbacks:
 * - Unrecognized status → `_unknown` config entry (with WARN log emission)
 * - Unrecognized kind → raw kind string as label
 * - Missing `updatedAt` → `_creationTime` as fallback
 */
export function transformWorkflowRun(
  doc: Doc<"workflowRuns">,
): RefinementRunCard {
  const status = doc.status as string;

  // Look up the status config, falling back to _unknown for unrecognized values
  let config = STATUS_CONFIG[status];
  if (!config) {
    config = STATUS_CONFIG["_unknown"]!;
    // Emit structured WARN log for observability (NFR-009)
    console.warn(
      JSON.stringify({
        event: "transform.unknownStatus",
        status,
        entity: "workflowRun",
        fallback: "_unknown",
      }),
    );
  }

  // Compute kindLabel via KIND_CONFIG with raw-string fallback (FR-011)
  const kindEntry = KIND_CONFIG[doc.kind];
  const kindLabel = kindEntry ? kindEntry.label : doc.kind;

  // Compute updatedAtMs: prefer updatedAt if present, fall back to _creationTime
  const updatedAtMs =
    (doc as Record<string, unknown>)["updatedAt"] != null
      ? ((doc as Record<string, unknown>)["updatedAt"] as number)
      : doc._creationTime;

  return {
    id: doc._id,

    // Status presentation
    statusKey: status,
    statusLabel: config.label,
    statusColor: config.color,
    statusBgColor: config.bgColor,

    // Derived flags — driven by TERMINAL_STATUSES, not hardcoded literals (FR-012)
    isTerminal: isTerminalStatus(status),

    // Kind
    kindLabel,
    kind: doc.kind,

    // Timestamps
    updatedAtMs,
    createdAtMs: doc.createdAt ?? doc._creationTime,

    // Run identity
    initiatedByUserId: doc.initiatedByUserId,
    maxReviewIterations: doc.maxReviewIterations,
    contentThreshold: doc.contentThreshold,
    structuralThreshold: doc.structuralThreshold,

    // Failure reason — surfaced on the RefinementRun screen when status is failed
    errorMessage: doc.errorMessage ?? null,
  };
}
